import { useCallback, useEffect, useMemo, useRef } from 'react';

/** Requests belong to a session and an opening of a target, including mutations. */
export default function useRequestOwnership(scope: string) {
  const owner = useRef({ scope, epoch: 0, mounted: true, tickets: new Map<string, symbol>() });
  if (owner.current.scope !== scope) {
    owner.current.scope = scope;
    owner.current.epoch += 1;
    owner.current.tickets.clear();
  }
  const epoch = owner.current.epoch;
  useEffect(() => {
    const current = owner.current;
    current.mounted = true;
    return () => { current.mounted = false; current.tickets.clear(); };
  }, []);
  const capture = useCallback((key: string) => {
    const ticket = owner.current.tickets.get(key);
    return () => Boolean(ticket) && owner.current.mounted && owner.current.scope === scope && owner.current.epoch === epoch
      && owner.current.tickets.get(key) === ticket;
  }, [scope, epoch]);
  const begin = useCallback((key: string) => {
    if (owner.current.scope === scope && owner.current.epoch === epoch && owner.current.mounted) owner.current.tickets.set(key, Symbol(key));
    return capture(key);
  }, [capture, scope, epoch]);
  const invalidate = useCallback((key: string) => {
    if (owner.current.scope === scope && owner.current.epoch === epoch) owner.current.tickets.delete(key);
  }, [scope, epoch]);
  return useMemo(() => ({ begin, capture, invalidate }), [begin, capture, invalidate]);
}
